// src/i18n/config.ts

// Core i18next library.
import i18n from 'i18next';
// Bindings for React: allow components to
// re-render when language changes.
import { initReactI18next } from 'react-i18next';

// Add names for each locale to
// show the user in our locale
// switcher.
export const supportedLngs = {
	en: "English",
	es: "Español",
	eu: "Euskera",
};

i18n
	// Add React bindings as a plugin.
	.use(initReactI18next)
	// Initialize the i18next instance.
	.init({
		// Config options

		// Specifies the default language (locale) used
		// when a user visits our site for the first time.
		// We use English here, but feel free to use
		// whichever locale you want.                   
		lng: (typeof localStorage !== 'undefined' && localStorage.getItem('i18nextLng')) || "es",

		// Fallback locale used when a translation is
		// missing in the active locale. Again, use your
		// preferred locale here. 
		fallbackLng: "en",

		// Enables useful output in the browser’s
		// dev console.
		debug: true,

		// Normally, we want `escapeValue: true` as it
		// ensures that i18next escapes any code in
		// translation messages, safeguarding against
		// XSS (cross-site scripting) attacks. However,
		// React does this escaping itself, so we turn 
		// it off in i18next.
		interpolation: {
			escapeValue: false,
		},

		// Translation messages. Add any languages
		// you want here.
		resources: {
			// English
			en: {
				translation: {
					// Common
					loading: "Loading...",
					error: "Error",
					cancel: "Cancel",
					save: "Save",
					delete: "Delete",
					edit: "Edit",
					create: "Create",
					update: "Update",
					search: "Search",
					filter: "Filter",
					totalProjects: "Total projects",
					activeProjects: "Active projects",
					completedProjects: "Completed projects",
					searchProjects: "Search projects...",
					allStatuses: "All statuses",
					noProjectsFound: "No projects found",
					noProjectsDescription: "There are no projects matching the current filters.",
					viewAllDamages: "View all damages",
					allDamages: "All damages",
					changeLanguage: "Change language",
					roleAdmin: "Administrator",
					roleProjectManager: "Project manager",
					roleWorker: "Worker",
					roleViewer: "Viewer",
					draft: "Draft",
					approved: "Approved",
					confirmDeleteWorkReport: "Delete this work report?",
					workReportNumber: "Report no. {{number}}",
					workReportTitle: "DIGITU work report no. {{number}}",
					sentBy: "Sent by",
					viewDetails: "View details",
					exportPdf: "Export PDF",
					exportExcel: "Export Excel",
					employeesCount: "{{count}} employees",
					machineryCount: "{{count}} machines",
					materialsCount: "{{count}} materials",
					loadingWorkReports: "Loading work reports...",
					exportPdfError: "The PDF could not be exported.",
					exportExcelError: "The Excel file could not be exported.",
					editWorkReport: "Edit work report",
					createWorkReportTitle: "Create work report",
					loadingCatalogs: "Loading catalogues...",
					generalInfo: "General information",
					ownStaff: "Own staff",
					ownMachinery: "Own machinery",
					rentedMachinery: "Rented machinery",
					laidMaterials: "Laid materials",
					conditionsNotes: "Conditions and notes",
					reportNumberAuto: "The report number is assigned automatically",
					weather: "Weather",
					ambientTemp: "Ambient temperature (°C)",
					atmosphere: "Conditions",
					surfaceState: "Surface condition",
					weatherDirect: "Sunny",
					weatherCloudy: "Cloudy",
					weatherRain: "Rain",
					weatherStorm: "Storm",
					weatherWind: "Wind",
					surfaceWet: "Damp",
					surfaceSoaked: "Wet",
					surfaceDry: "Dry",
					addStaff: "Add staff",
					addMachinery: "Add machinery",
					addRentedMachinery: "Add rented machinery",
					addMaterial: "Add material",
					createEmployees: "Create employees",
					createMachinery: "Create machinery",
					createMaterials: "Create materials",
					noEmployeesCatalog: "There are no employees in the catalogue.",
					noMachineryCatalog: "There is no machinery in the catalogue.",
					noMaterialsCatalog: "There are no materials in the catalogue.",
					noStaffAdded: "No staff added yet. Click “Add staff” to start.",
					createNewEmployee: "+ Create new employee",
					createNewMachinery: "+ Create new machinery",
					selectSupplier: "Select supplier...",
					selectMaterial: "Select material...",
					startTime: "Start time",
					endTime: "End time",
					stopDuration: "Stop duration (h)",
					grossShift: "Gross shift (h)",
					netShift: "Net shift (h)",
					grouping: "Grouping",
					days: "Days",
					hours: "Hours",
					machine: "Machine",
					supplier: "Supplier",
					extraDescription: "Extra description",
					observations: "Observations",
					images: "Images",
					attachments: "Attachments",
					savingEllipsis: "Saving...",
					staff: "Staff",
					mixTemperature: "Mix temperature",
					weatherAt9: "Weather at 9:00",
					millingTrucks: "Milling trucks",
					workTrucks: "Site trucks",
					asfaltiaMaterials: "Asfaltia laid materials",
					asfaltiaTrucks: "Asfaltia trucks",
					arrivalTime: "Arrival time",
					trips: "Trips",
					createdOn: "Created",
					lastModified: "Last modified",
					daysUnit: "{{count}} days",
					temperature: "Temperature",
					rentedMachineryItem: "Rented machinery {{index}}",
					catalogEmployees: "Employees",
					catalogMachinery: "Machinery",
					catalogSuppliers: "Suppliers",
					catalogTrucks: "Trucks",
					catalogMaterials: "Materials",
					code: "Code",
					lastName: "Last name",
					plate: "Plate",
					driver: "Driver",
					phone: "Phone",
					contact: "Contact",
					contactPerson: "Contact person",
					email: "Email",
					active: "Active",
					inactive: "Inactive",
					all: "All",
					actives: "Active",
					inactives: "Inactive",
					createNew: "+ Create new",
					saveChanges: "Save changes",
					selectOption: "Select...",
					noItems: "There are no items",
					actions: "Actions",
					requiredCode: "Code is required",
					requiredName: "Name is required",
					requiredLastName: "Last name is required",
					requiredType: "Type is required",
					requiredDescription: "Description is required",
					requiredPlate: "Plate is required",
					invalidEmail: "The email is not valid",
					loadDataError: "The data could not be loaded",
					connectionError: "Connection error",
					deleteItemError: "The item could not be deleted",
					saveItemError: "The item could not be saved",
					updateItemError: "The item could not be updated",
					confirmDeleteItem: "Delete this item?",
					deactivate: "Deactivate",
					activate: "Activate",
					editItem: "Edit {{title}}",
					createItem: "Create {{title}}",
					typeLabel: "Type",
					typeVehicle: "Vehicle",
					typeMiller: "Milling machine",
					typePaver: "Paver",
					typeCompactor: "Compactor",
					typeSweeper: "Sweeper",
					typeTruck: "Truck",
					typeOther: "Other",
					typeMilling: "Milling",
					typeSite: "Site",
					typeMix: "Mix",
					preview: "Preview",
					dimensionValue: "Value in {{unit}}",
					pdfGenerateError: "The PDF could not be generated",
					dateRequired: "Date is required",
					unknownError: "Unknown error",
					staffItem: "Staff {{index}}",
					machineryItem: "Machinery {{index}}",
					searchEmployee: "Search employee...",
					noEmployeesCreateFirst: "There are no employees. Create one first.",
					searchMachinery: "Search machinery...",
					noMachineryCreateFirst: "There is no machinery. Create one first.",
					autoCalculated: "Calculated automatically",
					groupingLaid: "Laying",
					groupingMilling: "Milling",
					groupingTackCoat: "Tack coats",
					groupingCompacted: "Compaction",
					groupingSwept: "Sweeping",
					groupingSignage: "Signage",
					groupingAgglomerateCoat: "Agglomerate coat",
					observationsPlaceholder: "Notes about the work done...",
					additionalInfo: "Additional information",
					noOptionsAvailable: "No options available",
					markAs: "Mark as:",
					statusInit: "Open",
					statusRevised: "Revised",
					statusFinished: "Finished",
					viewIn3d: "View in 3D",
					unnamed: "Unnamed",
					ifcViewer: "IFC viewer",
					close: "Close",
					openDamageMenu: "Open damage menu",
					closeDamageMenu: "Close damage menu",
					confirmRejection: "Confirm rejection",
					startQRScan: "Start QR scan",
					stopQRScan: "Stop QR scan",
					status: "Status",
					createdAt: "Created at",
					damagePosition: "Position",
					
					// Authentication
					signIn: "Sign In",
					signOut: "Sign Out",
					signingIn: "Signing in...",
					signInDescription: "Enter your credentials to access the platform",
					emailOrUsername: "Email or Username",
					enterEmailOrUsername: "Enter your email or username",
					password: "Password",
					enterPassword: "Enter your password",
					invalidCredentials: "Invalid credentials",
					pleaseEnterCredentials: "Please enter your credentials",
					needAccount: "Need an account?",
					contactAdmin: "Contact administrator",
					userMenu: "User menu",
					profile: "Profile",
					logout: "Logout",
					
					// Navigation
					backToDashboard: "Back to Dashboard",
					dashboard: "Dashboard",
					projects: "Projects",
					allProjects: "All Projects",
					projectNotFound: "Project not found",
					
					// Project related
					projectInformation: "Project Information",
					projectManager: "Project Manager",
					client: "Client",
					location: "Location",
					budget: "Budget",
					startDate: "Start Date",
					endDate: "End Date",
					noDescription: "No description available",
					
					// Project status
					planning: "Planning",
					inProgress: "In Progress",
					completed: "Completed",
					paused: "Paused",
					
					// Tabs
					overview: "Overview",
					damages: "Damages",
					workReports: "Work Reports",
					
					// Damages/Alerts
					alerts: "Alerts",
					alertUnit: "Alert Unit",
					alertType: "Alert Type",
					totalDamages: "Total Damages",
					recentDamages: "Recent Damages",
					completedWorkReports: "Completed Work Reports",
					noAlertsFound: "No alerts found",
					loadingAlerts: "Loading alerts...",
					noWorkReportsFound: "No Work Reports Found",
					noWorkReportsDescription: "No work reports have been created for this project yet.",
					createWorkReport: "Create Work Report",
					createFirstWorkReport: "Create First Work Report",
					
					// Stats
					correct: "Correct",
					incorrect: "Incorrect",
					
					// AR Session
					startARSession: "Start AR Session",
					aRDeviceNotSupported: "AR Device not supported",
					endARSession: "End AR Session",
					aRnoCompatible: "AR not compatible",
					
					// Building/Construction
					buildingHistory: "Building History",
					building: "Building",
					buildingAdjustmentOptions: "Building Adjustment Options",
					saveBuildingPosition: "Save Building Position",
					positionUpdated: "Position Updated",
					restartBuilding: "Restart building",
					scanBuilding: "Scan building",
					lookingForBuilding: "Looking for Building QR ...",
					readValidQR: "Read a valid QR in order to show the BIM model",
					
					// Panel/Component details
					panelStatus: "Panel Status", 
					panelDetails: "Panel Details",
					verifiyingPanelDetails: "Verifying Panel Details",
					clickPanelToVerify: "Click on a panel to verify...",
					scanPanelTovalidate: "Scan panel QR to validate...",
					scanPanel: "Scan panel",
					lookingForPanel: "Looking for panel QR ...",
					componentsOpacity: "Components opacity",
					expectedIFCid: "Expected IFC Id",
					foundedIFCid: "Found IFC Id",
					
					// Operations
					operations: "Operations",
					verify: "Verify",
					reject: "Reject",
					undo: "Undo",
					add: "Add",
					back: "Back",
					
					// Details
					description: "Description",
					date: "Date",
					name: "Name",
					state: "State",
					manufacturedOn: "Manufactured On",
					explicitDetails: "Specific Details",
					rejectReasons: "Reject Reasons",
					
					// Scanning
					scanningActive: "Scanning active...",
					
					// Misc
					pivotPoint: "Pivot Point",
					rotation: "Rotation",
					scale: "Scale",
					roadDamages: {
						count: "Damages",
						summary: "Summary",
						maxSeverity: "Maximum severity",
						downloadList: "Download damage list",
						chooseFormat: "Choose a format",
						downloadPdfOption: "Download PDF",
						downloadExcelOption: "Download Excel",
						attach: "Add new damage",
						loading: "Loading damages…",
						empty: "There are no damage records in this project yet.",
						elementFamily: "Element group",
						element: "Element",
						dimension: "Dimension",
						intensity: "Intensity",
						category: "Category",
						comment: "Comment",
						inspectedBy: "Inspected by",
						downloadPdf: "Download PDF",
						delete: "Delete",
						remediated: "Remediated",
						remediatedYes: "Yes",
						remediatedNo: "No",
						damageFixed: "Damage corrected",
						damageOpen: "Damage not remediated",
						markRemediated: "Mark as remediated",
						suggestRemediated: "Suggest as remediated",
						suggestTitle: "Suggest remediation",
						confirmSuggestion: "Send suggestion",
						waitingManager: "Waiting for the project manager",
						suggestionPending: "Suggested remediation",
						reviewSuggestion: "Review suggestion",
						suggestedBy: "Suggested by",
						acceptSuggestion: "Accept",
						rejectSuggestion: "Reject",
						requestDelete: "Request damage deletion",
						requestDeleteError: "The deletion request could not be sent.",
						confirmDeleteRequest: "Request deletion of damage ID:{{id}}?",
						waitingDelete: "Waiting for the project manager",
						deleteRequestPending: "Deletion request",
						reviewDelete: "Review deletion",
						requestedBy: "Requested by",
						acceptDelete: "Delete damage",
						remedyTitle: "Remediate damage",
						fromGallery: "Choose from gallery",
						confirmRemedy: "Save remediation",
						remedyImage: "Remediation photo",
						remedyRequired: "A photo of the remediated damage is required.",
						remedyError: "The remediation could not be saved.",
						title: "Add new damage",
						image: "Image",
						analyze: "Analyse image",
						analyzing: "Analysing image…",
						detectionAlt: "Detection",
						proposal: "The detector suggests {{damage}} with {{confidence}}% confidence.",
						notDetected: "None of the learned damages were detected.",
						incorrectManual: "Incorrect, enter manually",
						ok: "OK",
						enterManual: "Enter manually",
						damageType: "Damage type",
						specificDamage: "Specific damage",
						select: "Select…",
						optionalComment: "Comment (optional)",
						optionalVideo: "Video (optional)",
						attachVideo: "Record video",
						recordVideo: "Start recording",
						stopVideo: "Stop recording",
						removeVideo: "Remove video",
						videoMax: "Maximum 30 seconds. Optional.",
						videoSeconds: "{{seconds}} s / {{max}} s",
						playVideo: "Play video",
						videoError: "The video could not be loaded.",
						workOrder: "Work order",
						commentPlaceholder: "Operator notes…",
						severity: "Severity",
						registered: "Registered",
						save: "Save record",
						saving: "Saving and generating PDF…",
						confirmDelete: "Delete damage ID:{{id}}?",
						imageRequired: "Attach an image of the damage.",
						imageFileRequired: "The selected file must be an image.",
						imageReadError: "The image could not be read.",
						startingCamera: "Starting camera…",
						takePhoto: "Take photo",
						capturedPhoto: "Captured photo",
						retakePhoto: "Retake photo",
						switchToFrontCamera: "Use front camera",
						switchToRearCamera: "Use rear camera",
						cameraError: "The camera could not be accessed. Check the browser permission.",
						gettingLocation: "Getting the device location…",
						locationError: "The location could not be obtained. You may continue, but this damage will not appear on the map.",
						location: "Location",
						viewMap: "View damage map",
						mapTitle: "Project damage map",
						noMappedDamages: "No damage with coordinates has been saved in this project yet.",
						typeRequired: "Select the damage type.",
						dimensionRequired: "Enter a valid dimension in {{unit}}.",
						elementRequired: "Select the element.",
						intensityRequired: "Select the intensity (1 to 4).",
						loadError: "The damages stored in Strapi could not be loaded.",
						saveError: "The damage could not be saved.",
						deleteError: "The damage could not be deleted.",
						listError: "The damage list could not be downloaded.",
						pdfError: "The record could not be downloaded.",
						detectorUnavailable: "The detector is unavailable.",
						continueManual: "You can continue and enter the damage manually.",
						catalogMismatch: "The detected damage is not in the catalogue. Enter it manually.",
					},
				},
			},
			
			// Español
			es: {
				translation: {
					// Common
					loading: "Cargando...",
					error: "Error",
					cancel: "Cancelar",
					save: "Guardar",
					delete: "Eliminar",
					edit: "Editar",
					create: "Crear",
					update: "Actualizar",
					search: "Buscar",
					filter: "Filtrar",
					totalProjects: "Total de proyectos",
					activeProjects: "Proyectos activos",
					completedProjects: "Proyectos completados",
					searchProjects: "Buscar proyectos...",
					allStatuses: "Todos los estados",
					noProjectsFound: "No se encontraron proyectos",
					noProjectsDescription: "No hay proyectos que coincidan con los filtros actuales.",
					viewAllDamages: "Ver todos los daños",
					allDamages: "Todos los daños",
					changeLanguage: "Cambiar idioma",
					roleAdmin: "Administrador",
					roleProjectManager: "Jefe de proyecto",
					roleWorker: "Operario",
					roleViewer: "Visualizador",
					draft: "Borrador",
					approved: "Aprobado",
					confirmDeleteWorkReport: "¿Eliminar este parte de trabajo?",
					workReportNumber: "Parte n.º {{number}}",
					workReportTitle: "PARTE DE TRABAJO DIGITU N.º {{number}}",
					sentBy: "Enviado por",
					viewDetails: "Ver detalles",
					exportPdf: "Exportar PDF",
					exportExcel: "Exportar Excel",
					employeesCount: "{{count}} empleados",
					machineryCount: "{{count}} maquinaria",
					materialsCount: "{{count}} materiales",
					loadingWorkReports: "Cargando partes de trabajo...",
					exportPdfError: "No se pudo exportar el PDF.",
					exportExcelError: "No se pudo exportar el Excel.",
					editWorkReport: "Editar parte de trabajo",
					createWorkReportTitle: "Crear parte de trabajo",
					loadingCatalogs: "Cargando catálogos...",
					generalInfo: "Información general",
					ownStaff: "Personal propio",
					ownMachinery: "Maquinaria propia",
					rentedMachinery: "Maquinaria alquilada",
					laidMaterials: "Materiales extendido",
					conditionsNotes: "Condiciones y observaciones",
					reportNumberAuto: "El número de parte se asigna automáticamente",
					weather: "Climatología",
					ambientTemp: "Temperatura ambiente (°C)",
					atmosphere: "Ambiente",
					surfaceState: "Estado de la superficie",
					weatherDirect: "Despejado",
					weatherCloudy: "Nublado",
					weatherRain: "Lluvia",
					weatherStorm: "Tormenta",
					weatherWind: "Viento",
					surfaceWet: "Húmedo",
					surfaceSoaked: "Mojado",
					surfaceDry: "Seco",
					addStaff: "Añadir personal",
					addMachinery: "Añadir maquinaria",
					addRentedMachinery: "Añadir maquinaria alquilada",
					addMaterial: "Añadir material",
					createEmployees: "Crear empleados",
					createMachinery: "Crear maquinaria",
					createMaterials: "Crear materiales",
					noEmployeesCatalog: "No hay empleados en el catálogo.",
					noMachineryCatalog: "No hay maquinaria en el catálogo.",
					noMaterialsCatalog: "No hay materiales en el catálogo.",
					noStaffAdded: "No hay personal añadido. Pulsa “Añadir personal” para empezar.",
					createNewEmployee: "+ Crear nuevo empleado",
					createNewMachinery: "+ Crear nueva maquinaria",
					selectSupplier: "Seleccionar proveedor...",
					selectMaterial: "Seleccionar material...",
					startTime: "Hora de inicio",
					endTime: "Hora de fin",
					stopDuration: "Duración de paradas (h)",
					grossShift: "Jornada bruta (h)",
					netShift: "Jornada neta (h)",
					grouping: "Agrupación",
					days: "Días",
					hours: "Horas",
					machine: "Máquina",
					supplier: "Proveedor",
					extraDescription: "Descripción extra",
					observations: "Observaciones",
					images: "Imágenes",
					attachments: "Archivos adjuntos",
					savingEllipsis: "Guardando...",
					staff: "Personal",
					mixTemperature: "Temperatura de la mezcla",
					weatherAt9: "Climatología a las 9:00",
					millingTrucks: "Camiones de fresado",
					workTrucks: "Camiones de obra",
					asfaltiaMaterials: "Materiales Asfaltia extendido",
					asfaltiaTrucks: "Camiones Asfaltia",
					arrivalTime: "Hora de llegada",
					trips: "Viajes",
					createdOn: "Creado",
					lastModified: "Última modificación",
					daysUnit: "{{count}} días",
					temperature: "Temperatura",
					rentedMachineryItem: "Maquinaria alquilada {{index}}",
					catalogEmployees: "Empleados",
					catalogMachinery: "Maquinaria",
					catalogSuppliers: "Proveedores",
					catalogTrucks: "Camiones",
					catalogMaterials: "Materiales",
					code: "Código",
					lastName: "Apellidos",
					plate: "Matrícula",
					driver: "Conductor",
					phone: "Teléfono",
					contact: "Contacto",
					contactPerson: "Persona de contacto",
					email: "Email",
					active: "Activo",
					inactive: "Inactivo",
					all: "Todos",
					actives: "Activos",
					inactives: "Inactivos",
					createNew: "+ Crear nuevo",
					saveChanges: "Guardar cambios",
					selectOption: "Seleccionar...",
					noItems: "No hay elementos",
					actions: "Acciones",
					requiredCode: "El código es obligatorio",
					requiredName: "El nombre es obligatorio",
					requiredLastName: "Los apellidos son obligatorios",
					requiredType: "El tipo es obligatorio",
					requiredDescription: "La descripción es obligatoria",
					requiredPlate: "La matrícula es obligatoria",
					invalidEmail: "El email no es válido",
					loadDataError: "Error al cargar los datos",
					connectionError: "Error de conexión",
					deleteItemError: "Error al eliminar",
					saveItemError: "Error al guardar",
					updateItemError: "Error al actualizar",
					confirmDeleteItem: "¿Eliminar este elemento?",
					deactivate: "Desactivar",
					activate: "Activar",
					editItem: "Editar {{title}}",
					createItem: "Crear {{title}}",
					typeLabel: "Tipo",
					typeVehicle: "Vehículo",
					typeMiller: "Fresadora",
					typePaver: "Extendedora",
					typeCompactor: "Compactador",
					typeSweeper: "Barredora",
					typeTruck: "Camión",
					typeOther: "Otro",
					typeMilling: "Fresado",
					typeSite: "Obra",
					typeMix: "Mezcla",
					preview: "Vista previa",
					dimensionValue: "Valor en {{unit}}",
					pdfGenerateError: "No se pudo generar el PDF",
					dateRequired: "Fecha requerida",
					unknownError: "Error desconocido",
					staffItem: "Personal {{index}}",
					machineryItem: "Maquinaria {{index}}",
					searchEmployee: "Buscar empleado...",
					noEmployeesCreateFirst: "No hay empleados. Crea uno primero.",
					searchMachinery: "Buscar maquinaria...",
					noMachineryCreateFirst: "No hay maquinaria. Crea una primero.",
					autoCalculated: "Se calcula automáticamente",
					groupingLaid: "Extendido",
					groupingMilling: "Fresado",
					groupingTackCoat: "Riegos",
					groupingCompacted: "Compactado",
					groupingSwept: "Barrido",
					groupingSignage: "Señalización",
					groupingAgglomerateCoat: "Riego aglomerado",
					observationsPlaceholder: "Observaciones sobre el trabajo realizado...",
					additionalInfo: "Información adicional",
					noOptionsAvailable: "No hay opciones disponibles",
					markAs: "Marcar como:",
					statusInit: "Abierto",
					statusRevised: "Revisado",
					statusFinished: "Finalizado",
					viewIn3d: "Ver en 3D",
					unnamed: "Sin nombre",
					ifcViewer: "Visor IFC",
					close: "Cerrar",
					openDamageMenu: "Abrir menú de daños",
					closeDamageMenu: "Cerrar menú de daños",
					confirmRejection: "Confirmar rechazo",
					startQRScan: "Iniciar escaneo QR",
					stopQRScan: "Detener escaneo QR",
					status: "Estado",
					createdAt: "Creado",
					damagePosition: "Posición",
					
					// Authentication
					signIn: "Iniciar Sesión",
					signOut: "Cerrar Sesión",
					signingIn: "Iniciando sesión...",
					signInDescription: "Ingresa tus credenciales para acceder a la plataforma",
					emailOrUsername: "Email o Usuario",
					enterEmailOrUsername: "Ingresa tu email o usuario",
					password: "Contraseña",
					enterPassword: "Ingresa tu contraseña",
					invalidCredentials: "Credenciales inválidas",
					pleaseEnterCredentials: "Por favor ingresa tus credenciales",
					needAccount: "¿Necesitas una cuenta?",
					contactAdmin: "Contactar administrador",
					userMenu: "Menú de usuario",
					profile: "Perfil",
					logout: "Cerrar sesión",
					
					// Navigation
					backToDashboard: "Volver al Dashboard",
					dashboard: "Dashboard",
					projects: "Proyectos",
					allProjects: "Todos los Proyectos",
					projectNotFound: "Proyecto no encontrado",
					
					// Project related
					projectInformation: "Información del Proyecto",
					projectManager: "Director del Proyecto",
					client: "Cliente",
					location: "Ubicación",
					budget: "Presupuesto",
					startDate: "Fecha de Inicio",
					endDate: "Fecha de Fin",
					noDescription: "Sin descripción disponible",
					
					// Project status
					planning: "Planificación",
					inProgress: "En Progreso",
					completed: "Completado",
					paused: "Pausado",
					
					// Tabs
					overview: "Resumen",
					damages: "Daños",
					workReports: "Informes de Trabajo",
					
					// Damages/Alerts
					alerts: "Alertas",
					alertUnit: "Unidad de Alerta",
					alertType: "Tipo de Alerta",
					totalDamages: "Total de Daños",
					recentDamages: "Daños Recientes",
					completedWorkReports: "Informes de Trabajo Completados",
					noAlertsFound: "No se encontraron alertas",
					loadingAlerts: "Cargando alertas...",
					noWorkReportsFound: "No hay Partes de Trabajo",
					noWorkReportsDescription: "Aún no se han creado partes de trabajo para este proyecto",
					createWorkReport: "Crear Parte de Trabajo",
					createFirstWorkReport: "Crear Primer Parte de Trabajo",
					
					// Stats
					correct: "Correcto",
					incorrect: "Incorrecto",
					
					// AR Session
					startARSession: "Iniciar Sesión AR",
					aRDeviceNotSupported: "Dispositivo AR no compatible",
					endARSession: "Finalizar Sesión AR",
					aRnoCompatible: "AR no compatible",
					
					// Building/Construction
					buildingHistory: "Historial del Edificio",
					building: "Edificio",
					buildingAdjustmentOptions: "Opciones de Ajuste del Edificio",
					saveBuildingPosition: "Guardar Posición del Edificio",
					positionUpdated: "Posición Actualizada",
					restartBuilding: "Reiniciar edificio",
					scanBuilding: "Escanear edificio",
					lookingForBuilding: "Buscando QR del edificio...",
					readValidQR: "Lea un QR válido para mostrar el modelo BIM",
					
					// Panel/Component details
					panelStatus: "Estado del Panel",
					panelDetails: "Detalles del Panel",
					verifiyingPanelDetails: "Verificando Detalles del Panel",
					clickPanelToVerify: "Haz click en un panel para verificar...",
					scanPanelTovalidate: "Escanear QR del panel para validar...",
					scanPanel: "Escanear panel",
					lookingForPanel: "Buscando QR del panel...",
					componentsOpacity: "Opacidad de los Componentes",
					expectedIFCid: "ID IFC Esperado",
					foundedIFCid: "ID IFC Encontrado",
					
					// Operations
					operations: "Operaciones",
					verify: "Verificar",
					reject: "Rechazar",
					undo: "Deshacer",
					add: "Añadir",
					back: "Volver",
					
					// Details
					description: "Descripción",
					date: "Fecha",
					name: "Nombre",
					state: "Estado",
					manufacturedOn: "Fabricado en",
					explicitDetails: "Detalles Específicos",
					rejectReasons: "Razones de Rechazo",
					
					// Scanning
					scanningActive: "Escáner activo...",
					
					// Misc
					pivotPoint: "Punto de Pivote",
					rotation: "Rotación",
					scale: "Escala",
					roadDamages: {
						count: "Daños",
						summary: "Resumen",
						maxSeverity: "Gravedad máxima",
						downloadList: "Descargar listado de daños",
						chooseFormat: "Elige el formato",
						downloadPdfOption: "Descargar PDF",
						downloadExcelOption: "Descargar Excel",
						attach: "Adjuntar nuevo daño",
						loading: "Cargando daños…",
						empty: "Todavía no hay fichas de daño en este proyecto.",
						elementFamily: "Categoría de elemento",
						element: "Elemento",
						dimension: "Dimensión",
						intensity: "Intensidad",
						category: "Categoría",
						comment: "Comentario",
						inspectedBy: "Inspeccionado por",
						downloadPdf: "Descargar PDF",
						delete: "Eliminar",
						remediated: "Subsanado",
						remediatedYes: "Sí",
						remediatedNo: "No",
						damageFixed: "Daño corregido",
						damageOpen: "Daño no subsanado",
						markRemediated: "Marcar como subsanado",
						suggestRemediated: "Sugerir daño como subsanado",
						suggestTitle: "Sugerir subsanación",
						confirmSuggestion: "Enviar sugerencia",
						waitingManager: "Esperando respuesta de jefe de proyecto",
						suggestionPending: "Sugerencia de daño subsanado",
						reviewSuggestion: "Revisar sugerencia",
						suggestedBy: "Sugerido por",
						acceptSuggestion: "Aceptar",
						rejectSuggestion: "Rechazar",
						requestDelete: "Solicitar eliminar daño",
						requestDeleteError: "No se pudo enviar la solicitud de eliminación.",
						confirmDeleteRequest: "¿Solicitar la eliminación del daño ID:{{id}}?",
						waitingDelete: "Esperando respuesta de jefe de proyecto",
						deleteRequestPending: "Solicitud de eliminación",
						reviewDelete: "Revisar eliminación",
						requestedBy: "Solicitado por",
						acceptDelete: "Eliminar daño",
						remedyTitle: "Subsanar daño",
						fromGallery: "Elegir de la galería",
						confirmRemedy: "Guardar subsanación",
						remedyImage: "Foto de la subsanación",
						remedyRequired: "Hace falta una foto del daño subsanado.",
						remedyError: "No se ha podido guardar la subsanación.",
						title: "Adjuntar nuevo daño",
						image: "Imagen",
						analyze: "Analizar imagen",
						analyzing: "Analizando imagen…",
						detectionAlt: "Detección",
						proposal: "El detector propone {{damage}} con una confianza del {{confidence}}%.",
						notDetected: "No se ha detectado ninguno de los daños aprendidos.",
						incorrectManual: "Incorrecto, introducir manualmente",
						ok: "OK",
						enterManual: "Introducir manualmente",
						damageType: "Tipo de daño",
						specificDamage: "Daño concreto",
						select: "Selecciona…",
						optionalComment: "Comentario (opcional)",
						optionalVideo: "Vídeo (opcional)",
						attachVideo: "Grabar vídeo",
						recordVideo: "Empezar grabación",
						stopVideo: "Parar grabación",
						removeVideo: "Quitar vídeo",
						videoMax: "Máximo 30 segundos. Opcional.",
						videoSeconds: "{{seconds}} s / {{max}} s",
						playVideo: "Reproducir vídeo",
						videoError: "No se ha podido cargar el vídeo.",
						workOrder: "Orden de trabajo",
						commentPlaceholder: "Observaciones del operario…",
						severity: "Gravedad",
						registered: "Registrado",
						save: "Guardar ficha",
						saving: "Guardando y generando PDF…",
						confirmDelete: "¿Eliminar el daño ID:{{id}}?",
						imageRequired: "Adjunta una imagen del daño.",
						imageFileRequired: "El archivo tiene que ser una imagen.",
						imageReadError: "No se ha podido leer la imagen.",
						startingCamera: "Iniciando cámara…",
						takePhoto: "Sacar foto",
						capturedPhoto: "Foto capturada",
						retakePhoto: "Repetir foto",
						switchToFrontCamera: "Usar cámara delantera",
						switchToRearCamera: "Usar cámara trasera",
						cameraError: "No se ha podido acceder a la cámara. Revisa el permiso del navegador.",
						gettingLocation: "Obteniendo ubicación del dispositivo…",
						locationError: "No se ha podido obtener la ubicación. Puedes continuar, pero este daño no aparecerá en el mapa.",
						location: "Ubicación",
						viewMap: "Ver mapa de daños",
						mapTitle: "Mapa de daños del proyecto",
						noMappedDamages: "Todavía no hay daños con coordenadas guardados en este proyecto.",
						typeRequired: "Elige el tipo de daño.",
						dimensionRequired: "Indica una dimensión válida en {{unit}}.",
						elementRequired: "Elige el elemento.",
						intensityRequired: "Elige la intensidad (1 a 4).",
						loadError: "No se pudieron cargar los daños guardados en Strapi.",
						saveError: "No se pudo guardar el daño.",
						deleteError: "No se pudo eliminar el daño.",
						listError: "No se pudo descargar el listado.",
						pdfError: "No se pudo descargar la ficha.",
						detectorUnavailable: "El detector no está disponible.",
						continueManual: "Puedes continuar e introducir el daño a mano.",
						catalogMismatch: "El daño detectado no está en el catálogo. Introdúcelo a mano.",
					},
				},
			},
			
			// Euskera
			eu: {
				translation: {
					// Common
					loading: "Kargatzen...",
					error: "Errorea",
					cancel: "Ezeztatu",
					save: "Gorde",
					delete: "Ezabatu",
					edit: "Editatu",
					create: "Sortu",
					update: "Eguneratu",
					search: "Bilatu",
					filter: "Iragazi",
					totalProjects: "Proiektuak guztira",
					activeProjects: "Proiektu aktiboak",
					completedProjects: "Proiektu osatuak",
					searchProjects: "Bilatu proiektuak...",
					allStatuses: "Egoera guztiak",
					noProjectsFound: "Ez da proiekturik aurkitu",
					noProjectsDescription: "Ez dago iragazkiekin bat datorren proiekturik.",
					viewAllDamages: "Ikusi kalte guztiak",
					allDamages: "Kalte guztiak",
					changeLanguage: "Aldatu hizkuntza",
					roleAdmin: "Administratzailea",
					roleProjectManager: "Proiektu-burua",
					roleWorker: "Langilea",
					roleViewer: "Ikustailea",
					draft: "Zirriborroa",
					approved: "Onartuta",
					confirmDeleteWorkReport: "Lan-parte hau ezabatu?",
					workReportNumber: "Parte zk. {{number}}",
					workReportTitle: "DIGITU LAN-PARTEA ZK. {{number}}",
					sentBy: "Nork bidalia",
					viewDetails: "Ikusi xehetasunak",
					exportPdf: "Esportatu PDFa",
					exportExcel: "Esportatu Excela",
					employeesCount: "{{count}} langile",
					machineryCount: "{{count}} makina",
					materialsCount: "{{count}} material",
					loadingWorkReports: "Lan-parteak kargatzen...",
					exportPdfError: "Ezin izan da PDFa esportatu.",
					exportExcelError: "Ezin izan da Excela esportatu.",
					editWorkReport: "Editatu lan-partea",
					createWorkReportTitle: "Sortu lan-partea",
					loadingCatalogs: "Katalogoak kargatzen...",
					generalInfo: "Informazio orokorra",
					ownStaff: "Norberaren langileak",
					ownMachinery: "Norberaren makineria",
					rentedMachinery: "Alokatutako makineria",
					laidMaterials: "Hedatutako materialak",
					conditionsNotes: "Baldintzak eta oharrak",
					reportNumberAuto: "Parte-zenbakia automatikoki esleitzen da",
					weather: "Eguraldia",
					ambientTemp: "Inguruko tenperatura (°C)",
					atmosphere: "Giroa",
					surfaceState: "Azalera-egoera",
					weatherDirect: "Eguzkitsua",
					weatherCloudy: "Hodeitsua",
					weatherRain: "Euria",
					weatherStorm: "Ekaitza",
					weatherWind: "Haizea",
					surfaceWet: "Hezea",
					surfaceSoaked: "Bustia",
					surfaceDry: "Lehorra",
					addStaff: "Gehitu langileak",
					addMachinery: "Gehitu makineria",
					addRentedMachinery: "Gehitu alokatutako makineria",
					addMaterial: "Gehitu materiala",
					createEmployees: "Sortu langileak",
					createMachinery: "Sortu makineria",
					createMaterials: "Sortu materialak",
					noEmployeesCatalog: "Ez dago langilerik katalogoan.",
					noMachineryCatalog: "Ez dago makineriarik katalogoan.",
					noMaterialsCatalog: "Ez dago materialik katalogoan.",
					noStaffAdded: "Oraindik ez da langilerik gehitu. Sakatu “Gehitu langileak” hasteko.",
					createNewEmployee: "+ Sortu langile berria",
					createNewMachinery: "+ Sortu makineria berria",
					selectSupplier: "Hautatu hornitzailea...",
					selectMaterial: "Hautatu materiala...",
					startTime: "Hasiera-ordua",
					endTime: "Amaiera-ordua",
					stopDuration: "Geldialdien iraupena (h)",
					grossShift: "Jardunaldi gordina (h)",
					netShift: "Jardunaldi garbia (h)",
					grouping: "Taldekatzea",
					days: "Egunak",
					hours: "Orduak",
					machine: "Makina",
					supplier: "Hornitzailea",
					extraDescription: "Deskribapen gehigarria",
					observations: "Oharrak",
					images: "Irudiak",
					attachments: "Eranskinak",
					savingEllipsis: "Gordetzen...",
					staff: "Langileak",
					mixTemperature: "Nahastearen tenperatura",
					weatherAt9: "Eguraldia 9:00etan",
					millingTrucks: "Fresatzeko kamioiak",
					workTrucks: "Obrako kamioiak",
					asfaltiaMaterials: "Asfaltiaren hedatutako materialak",
					asfaltiaTrucks: "Asfaltiaren kamioiak",
					arrivalTime: "Iritsiera-ordua",
					trips: "Bidaiak",
					createdOn: "Sortua",
					lastModified: "Azken aldaketa",
					daysUnit: "{{count}} egun",
					temperature: "Tenperatura",
					rentedMachineryItem: "Alokatutako makineria {{index}}",
					catalogEmployees: "Langileak",
					catalogMachinery: "Makineria",
					catalogSuppliers: "Hornitzaileak",
					catalogTrucks: "Kamioiak",
					catalogMaterials: "Materialak",
					code: "Kodea",
					lastName: "Abizenak",
					plate: "Matrikula",
					driver: "Gidaria",
					phone: "Telefonoa",
					contact: "Kontaktua",
					contactPerson: "Kontaktu-pertsona",
					email: "Emaila",
					active: "Aktibo",
					inactive: "Inaktibo",
					all: "Guztiak",
					actives: "Aktiboak",
					inactives: "Inaktiboak",
					createNew: "+ Sortu berria",
					saveChanges: "Gorde aldaketak",
					selectOption: "Hautatu...",
					noItems: "Ez dago elementurik",
					actions: "Ekintzak",
					requiredCode: "Kodea nahitaezkoa da",
					requiredName: "Izena nahitaezkoa da",
					requiredLastName: "Abizenak nahitaezkoak dira",
					requiredType: "Mota nahitaezkoa da",
					requiredDescription: "Deskribapena nahitaezkoa da",
					requiredPlate: "Matrikula nahitaezkoa da",
					invalidEmail: "Emaila ez da baliozkoa",
					loadDataError: "Ezin izan dira datuak kargatu",
					connectionError: "Konexio-errorea",
					deleteItemError: "Ezin izan da ezabatu",
					saveItemError: "Ezin izan da gorde",
					updateItemError: "Ezin izan da eguneratu",
					confirmDeleteItem: "Elementu hau ezabatu?",
					deactivate: "Desaktibatu",
					activate: "Aktibatu",
					editItem: "Editatu {{title}}",
					createItem: "Sortu {{title}}",
					typeLabel: "Mota",
					typeVehicle: "Ibilgailua",
					typeMiller: "Fresagailua",
					typePaver: "Hedagailua",
					typeCompactor: "Trinkotzailea",
					typeSweeper: "Erratzailea",
					typeTruck: "Kamioia",
					typeOther: "Beste bat",
					typeMilling: "Fresatzea",
					typeSite: "Obra",
					typeMix: "Nahastea",
					preview: "Aurrebista",
					dimensionValue: "Balioa {{unit}} unitatean",
					pdfGenerateError: "Ezin izan da PDFa sortu",
					dateRequired: "Data beharrezkoa da",
					unknownError: "Errore ezezaguna",
					staffItem: "Langileak {{index}}",
					machineryItem: "Makineria {{index}}",
					searchEmployee: "Bilatu langilea...",
					noEmployeesCreateFirst: "Ez dago langilerik. Sortu bat lehenik.",
					searchMachinery: "Bilatu makineria...",
					noMachineryCreateFirst: "Ez dago makineriarik. Sortu bat lehenik.",
					autoCalculated: "Automatikoki kalkulatzen da",
					groupingLaid: "Hedatzea",
					groupingMilling: "Fresatzea",
					groupingTackCoat: "Ihiztatzeak",
					groupingCompacted: "Trinkotzea",
					groupingSwept: "Erratzea",
					groupingSignage: "Seinalizazioa",
					groupingAgglomerateCoat: "Aglomeratu-ihiztatzea",
					observationsPlaceholder: "Egindako lanari buruzko oharrak...",
					additionalInfo: "Informazio gehigarria",
					noOptionsAvailable: "Ez dago aukerarik",
					markAs: "Markatu honela:",
					statusInit: "Irekita",
					statusRevised: "Berrikusia",
					statusFinished: "Amaituta",
					viewIn3d: "Ikusi 3D-n",
					unnamed: "Izenik gabe",
					ifcViewer: "IFC ikuslea",
					close: "Itxi",
					openDamageMenu: "Ireki kalteen menua",
					closeDamageMenu: "Itxi kalteen menua",
					confirmRejection: "Berretsi ukapena",
					startQRScan: "Hasi QR eskaneatzea",
					stopQRScan: "Gelditu QR eskaneatzea",
					status: "Egoera",
					createdAt: "Sortua",
					damagePosition: "Kokapena",
					
					// Authentication
					signIn: "Saioa Hasi",
					signOut: "Saioa Itxi",
					signingIn: "Saioa hasten...",
					signInDescription: "Sartu zure kredentzialak plataformara sartzeko",
					emailOrUsername: "Email edo Erabiltzailea",
					enterEmailOrUsername: "Sartu zure email edo erabiltzailea",
					password: "Pasahitza",
					enterPassword: "Sartu zure pasahitza",
					invalidCredentials: "Kredentziala baliogabeak",
					pleaseEnterCredentials: "Mesedez sartu zure kredentzialak",
					needAccount: "Kontu bat behar duzu?",
					contactAdmin: "Administratzailearekin harremanetan jarri",
					userMenu: "Erabiltzaile menua",
					profile: "Profila",
					logout: "Saioa itxi",
					
					// Navigation
					backToDashboard: "Dashboardera Itzuli",
					dashboard: "Dashboard",
					projects: "Proiektuak",
					allProjects: "Proiektu Guztiak",
					projectNotFound: "Ez da proiekturik aurkitu",
					
					// Project related
					projectInformation: "Proiektuaren Informazioa",
					projectManager: "Proiektu Kudeatzailea",
					client: "Bezeroa",
					location: "Kokapena",
					budget: "Aurrekontua",
					startDate: "Hasiera Data",
					endDate: "Amaiera Data",
					noDescription: "Ez dago deskribapenik eskuragarri",
					
					// Project status
					planning: "Planifikazioa",
					inProgress: "Aurrerapenean",
					completed: "Osatuta",
					paused: "Pausatuta",
					
					// Tabs
					overview: "Laburpena",
					damages: "Kalteak",
					workReports: "Lan Txostenak",
					
					// Damages/Alerts
					alerts: "Alertak",
					alertUnit: "Alerta Unitatea",
					alertType: "Alerta Mota",
					totalDamages: "Kalte Guztiak",
					recentDamages: "Azken Kalteak",
					completedWorkReports: "Osatutako Lan Txostenak",
					noAlertsFound: "Ez da alertarik aurkitu",
					loadingAlerts: "Alertak kargatzen...",
					noWorkReportsFound: "Ez dago Lan Txostenik",
					noWorkReportsDescription: "Oraindik ez da lan txostenik sortu proiektu honetarako",
					createWorkReport: "Lan Txostena Sortu",
					createFirstWorkReport: "Lehen Lan Txostena Sortu",
					
					// Stats
					correct: "Zuzena",
					incorrect: "Okerra",
					
					// AR Session
					startARSession: "AR Saioa Hasi",
					aRDeviceNotSupported: "AR gailua ez da onartzen",
					endARSession: "AR Saioa Amaitu",
					aRnoCompatible: "AR ez da bateragarria",
					
					// Building/Construction
					buildingHistory: "Eraikinaren Historia",
					building: "Eraikina",
					buildingAdjustmentOptions: "Eraikinaren Doikuntza Aukerak",
					saveBuildingPosition: "Eraikinaren Posizioa Gorde",
					positionUpdated: "Posizioa Eguneratuta",
					restartBuilding: "Eraikina berrabiarazi",
					scanBuilding: "Eraikina eskaneatu",
					lookingForBuilding: "Eraikinaren QRa bilatzen...",
					readValidQR: "Irakurri QR baliozkoa BIM eredua erakusteko",
					
					// Panel/Component details
					panelStatus: "Panelaren Egoera",
					panelDetails: "Panelaren Xehetasunak",
					verifiyingPanelDetails: "Panelaren Xehetasunak Egiaztatzen",
					clickPanelToVerify: "Klikatu panel batean egiaztatzeko...",
					scanPanelTovalidate: "Eskaneatu panelaren QRa balidatzeko...",
					scanPanel: "Panela eskaneatu",
					lookingForPanel: "Panelaren QRa bilatzen...",
					componentsOpacity: "Osagaien Gardentasuna",
					expectedIFCid: "Esperotako IFC IDa",
					foundedIFCid: "Aurkitutako IFC IDa",
					
					// Operations
					operations: "Eragiketak",
					verify: "Egiaztatu",
					reject: "Ukatu",
					undo: "Desegin",
					add: "Gehitu",
					back: "Itzuli",
					
					// Details
					description: "Deskribapena",
					date: "Data",
					name: "Izena",
					state: "Egoera",
					manufacturedOn: "Fabrikatua",
					explicitDetails: "Xehetasun Zehatzak",
					rejectReasons: "Ukatzeko Arrazoinak",
					
					// Scanning
					scanningActive: "Eskanerra aktibo...",
					
					// Misc
					pivotPoint: "Pibote Puntua",
					rotation: "Biraketa",
					scale: "Eskala",
					roadDamages: {
						count: "Kalteak",
						summary: "Laburpena",
						maxSeverity: "Larritasun handiena",
						downloadList: "Deskargatu kalteen zerrenda",
						chooseFormat: "Aukeratu formatua",
						downloadPdfOption: "Deskargatu PDFa",
						downloadExcelOption: "Deskargatu Excela",
						attach: "Erantsi kalte berria",
						loading: "Kalteak kargatzen…",
						empty: "Oraindik ez dago kalte-fitxarik proiektu honetan.",
						elementFamily: "Elementu-kategoria",
						element: "Elementua",
						dimension: "Dimentsioa",
						intensity: "Intentsitatea",
						category: "Kategoria",
						comment: "Iruzkina",
						inspectedBy: "Ikuskatu du",
						downloadPdf: "Deskargatu PDFa",
						delete: "Ezabatu",
						remediated: "Zuzenduta",
						remediatedYes: "Bai",
						remediatedNo: "Ez",
						damageFixed: "Kaltea zuzenduta",
						damageOpen: "Kaltea zuzendu gabe",
						markRemediated: "Zuzendutzat markatu",
						suggestRemediated: "Zuzendutzat iradoki",
						suggestTitle: "Zuzentzea iradoki",
						confirmSuggestion: "Iradokizuna bidali",
						waitingManager: "Proiektu-buruaren erantzunaren zain",
						suggestionPending: "Kaltea zuzentzeko iradokizuna",
						reviewSuggestion: "Iradokizuna berrikusi",
						suggestedBy: "Iradoki du",
						acceptSuggestion: "Onartu",
						rejectSuggestion: "Ukatu",
						requestDelete: "Kaltea ezabatzea eskatu",
						requestDeleteError: "Ezin izan da ezabatzeko eskaera bidali.",
						confirmDeleteRequest: "ID:{{id}} kaltea ezabatzea eskatu?",
						waitingDelete: "Proiektu-buruaren erantzunaren zain",
						deleteRequestPending: "Ezabatzeko eskaera",
						reviewDelete: "Ezabaketa berrikusi",
						requestedBy: "Eskatu du",
						acceptDelete: "Kaltea ezabatu",
						remedyTitle: "Kaltea zuzendu",
						fromGallery: "Galeriatik aukeratu",
						confirmRemedy: "Zuzentzea gorde",
						remedyImage: "Zuzentzearen argazkia",
						remedyRequired: "Zuzendutako kaltearen argazkia behar da.",
						remedyError: "Ezin izan da zuzentzea gorde.",
						title: "Erantsi kalte berria",
						image: "Irudia",
						analyze: "Aztertu irudia",
						analyzing: "Irudia aztertzen…",
						detectionAlt: "Detekzioa",
						proposal: "Detektagailuak {{damage}} proposatzen du, %{{confidence}}eko konfiantzarekin.",
						notDetected: "Ez da ikasitako kalterik detektatu.",
						incorrectManual: "Okerra, sartu eskuz",
						ok: "Ados",
						enterManual: "Sartu eskuz",
						damageType: "Kalte mota",
						specificDamage: "Kalte zehatza",
						select: "Hautatu…",
						optionalComment: "Iruzkina (aukerakoa)",
						optionalVideo: "Bideoa (aukerakoa)",
						attachVideo: "Grabatu bideoa",
						recordVideo: "Hasi grabazioa",
						stopVideo: "Gelditu grabazioa",
						removeVideo: "Kendu bideoa",
						videoMax: "Gehienez 30 segundo. Aukerakoa.",
						videoSeconds: "{{seconds}} s / {{max}} s",
						playVideo: "Erreproduzitu bideoa",
						videoError: "Ezin izan da bideoa kargatu.",
						workOrder: "Lan-agindua",
						commentPlaceholder: "Langilearen oharrak…",
						severity: "Larritasuna",
						registered: "Erregistratua",
						save: "Gorde fitxa",
						saving: "Gordetzen eta PDFa sortzen…",
						confirmDelete: "ID:{{id}} kaltea ezabatu?",
						imageRequired: "Erantsi kaltearen irudi bat.",
						imageFileRequired: "Hautatutako fitxategiak irudia izan behar du.",
						imageReadError: "Ezin izan da irudia irakurri.",
						startingCamera: "Kamera abiarazten…",
						takePhoto: "Atera argazkia",
						capturedPhoto: "Ateratako argazkia",
						retakePhoto: "Errepikatu argazkia",
						switchToFrontCamera: "Erabili aurreko kamera",
						switchToRearCamera: "Erabili atzeko kamera",
						cameraError: "Ezin izan da kamera erabili. Egiaztatu nabigatzailearen baimena.",
						gettingLocation: "Gailuaren kokapena lortzen…",
						locationError: "Ezin izan da kokapena lortu. Jarrai dezakezu, baina kalte hau ez da mapan agertuko.",
						location: "Kokapena",
						viewMap: "Ikusi kalteen mapa",
						mapTitle: "Proiektuko kalteen mapa",
						noMappedDamages: "Oraindik ez dago koordenatuak dituen kalterik proiektu honetan.",
						typeRequired: "Hautatu kalte mota.",
						dimensionRequired: "Adierazi baliozko dimentsioa {{unit}} unitatean.",
						elementRequired: "Hautatu elementua.",
						intensityRequired: "Hautatu intentsitatea (1etik 4ra).",
						loadError: "Ezin izan dira Strapin gordetako kalteak kargatu.",
						saveError: "Ezin izan da kaltea gorde.",
						deleteError: "Ezin izan da kaltea ezabatu.",
						listError: "Ezin izan da kalteen zerrenda deskargatu.",
						pdfError: "Ezin izan da fitxa deskargatu.",
						detectorUnavailable: "Detektagailua ez dago erabilgarri.",
						continueManual: "Jarraitu eta kaltea eskuz sar dezakezu.",
						catalogMismatch: "Detektatutako kaltea ez dago katalogoan. Sartu eskuz.",
					},
				},
			},
		},
	});

export default i18n;